// Mimecast's external-sender warning, removed from an inbound email body.
// Shared by the server (channels/m365/sanitize.js, the text the AI reads) and
// the agent UI (App.jsx's m365DisplayBodyHtml, what a CSR sees), so the two
// can't disagree about what the banner is.
//
// Mimecast stamps "This message originated outside your organization." into
// the BODY of every external message before Exchange delivers it, so it
// arrives as part of the customer's own email: a red heading in the thread and
// the first line of every message the drafter reads. It can't be switched off
// for the CXHub mailboxes on the Mimecast side, so it is removed here.
//
// Only a banner that is the FIRST visible text of the body counts. The same
// sentence further down is something a person wrote or quoted, and is left
// alone. When the banner sits in its own element (Mimecast wraps it in a box
// with a rule under it), the outermost element holding nothing but the banner
// goes with it, so no empty box is left behind; an <hr>/<br> spacer directly
// after it goes too. When the banner shares an element with the message, only
// the sentence is removed. Never throws: a body we can't walk comes back
// unchanged.
//
// Pure string work, no DOM: the UI's tests run under node --test.

const WORDS = ['This', 'message', 'originated', 'outside', 'your', 'organization']
// Between words: whitespace, a non-breaking space, or an inline tag (Mimecast
// and Outlook both like to wrap single words in spans).
const GAP = '(?:\\s|&nbsp;|&#160;|&#xa0;|<[^>]*>)+'
const BANNER_RE = new RegExp(WORDS.join(GAP) + '(?:(?:<[^>]*>)*\\.)?', 'i')

const TAG_RE = /<(\/?)([a-zA-Z][\w:-]*)\b[^>]*?(\/?)>/g
const VOID_TAGS = new Set(['area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input', 'link', 'meta', 'source', 'track', 'wbr'])
// Never removed as "the banner's element", even when they hold nothing else.
const DOCUMENT_TAGS = new Set(['html', 'head', 'body'])
const TRAILING_SPACER_RE = /^(?:\s|&nbsp;|&#160;|&#xa0;|<br\b[^>]*>|<hr\b[^>]*>)*/i

// Same-length blanking of content that is never visible text and may contain
// a stray "<" or ">" (CSS selectors, comments), so offsets stay valid.
function blankInvisible(html) {
  const blank = (m) => ' '.repeat(m.length)
  return html
    .replace(/<!--[\s\S]*?-->/g, blank)
    .replace(/<head\b[\s\S]*?<\/head>/gi, blank)
    .replace(/<style\b[\s\S]*?<\/style>/gi, blank)
    .replace(/<script\b[\s\S]*?<\/script>/gi, blank)
}

function hasVisibleText(fragment) {
  return fragment
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;|&#160;|&#xa0;/gi, '')
    .replace(/[\s​-‍⁠﻿­]/g, '')
    .length > 0
}

// Close the most recent open element named `name`, and anything opened inside
// it that was never closed. Returns the popped entries, innermost first.
function closeElement(stack, name) {
  for (let i = stack.length - 1; i >= 0; i--) {
    if (stack[i].name === name) return stack.splice(i).reverse()
  }
  return []
}

export function stripExternalSenderBanner(body) {
  if (!body) return body ?? ''
  const html = String(body)
  try {
    const work = blankInvisible(html)
    const match = BANNER_RE.exec(work)
    if (!match) return html
    const start = match.index
    const end = start + match[0].length
    if (hasVisibleText(work.slice(0, start))) return html

    // Elements open at the banner, in document order. An image before the
    // banner is content (an inline image is part of the email), so no element
    // opened before one may be removed: `floor` is where removal may begin.
    const stack = []
    let floor = 0
    TAG_RE.lastIndex = 0
    let t
    while ((t = TAG_RE.exec(work)) && t.index < start) {
      const [, closing, rawName, selfClosing] = t
      const name = rawName.toLowerCase()
      if (name === 'img') floor = t.index + t[0].length
      else if (closing) closeElement(stack, name)
      else if (!VOID_TAGS.has(name) && !selfClosing) stack.push({ name, start: t.index, beforeBanner: true })
    }

    // Walk on from the banner until visible content appears. Every element
    // that was open at the banner and closes before then holds nothing but
    // the banner; the last such one to close is the outermost.
    let cutFrom = start
    let cutTo = end
    let pos = end
    TAG_RE.lastIndex = end
    while ((t = TAG_RE.exec(work))) {
      if (hasVisibleText(work.slice(pos, t.index))) break
      const [, closing, rawName, selfClosing] = t
      const name = rawName.toLowerCase()
      if (name === 'img') break
      if (closing) {
        const popped = closeElement(stack, name).filter((e) => e.beforeBanner)
        if (popped.some((e) => DOCUMENT_TAGS.has(e.name) || e.start < floor)) break
        if (popped.length) {
          cutFrom = popped[popped.length - 1].start
          cutTo = t.index + t[0].length
        }
      } else if (!VOID_TAGS.has(name) && !selfClosing) {
        stack.push({ name, start: t.index, beforeBanner: false })
      }
      pos = t.index + t[0].length
    }

    const spacer = TRAILING_SPACER_RE.exec(html.slice(cutTo))[0].length
    return html.slice(0, cutFrom) + html.slice(cutTo + spacer)
  } catch {
    return html
  }
}
